import { PublicRoute } from 'config/routes'
import { useAuthContext, useRepository } from 'hooks/auth'
import { StoreProvider, useRootStore } from 'hooks/rootStore'
import { LoginPage } from 'pages/login/LoginPage'
import React, { useEffect, useState } from 'react'
import { RootStore } from 'store/root'
import { LazyMotion, domAnimation } from 'framer-motion'
import { MobxRouter } from './MobxRouter'
import { Theme } from './Theme'
import { I18nWrapper } from 'i18n/components'
import { UserContextProvider } from 'hooks/profileContext'
import { SettingsContextProvider } from 'hooks/settings'
import { observer } from 'mobx-react-lite'
import { NotFoundPage } from 'pages/application/NotFoundPage'
import { StorageUrlContextProvider } from 'components/contexts/StorageUrlContextProvider'
import { DialogMount } from 'components/dialogs/DialogMount'
import { DialogContextProvider } from 'components/dialogs/DialogContextProvider'
import { ObjectUrlContextProvider } from 'components/contexts/ObjectUrlContextProvider'
import { AuthedRedirectHandler } from './AuthedRedirectHandler'
import { useLTIMode } from 'hooks/use-lti-mode'
import classNames from 'classnames'
import { QAToolsProvider } from 'qatools/QAToolsProvider'
import { NewVersionBannerEntry } from './NewVersionBannerEntry'
import { ImpersonationBar } from 'components/ImpersonationBar'
import { KillSwitchEnabledPage } from 'pages/application/KillSwitchEnabledPage'
import { useTranslationTyped } from 'i18n/i18n'
import { UserAgreements } from './UserAgreements'
import { SchemaErrorNeedsUpdate } from './SchemaErrorNeedsUpdate'
import { useGradeSyncNotification } from 'hooks/useGradeSyncNotification'
import { inE2E } from 'config/environment'
import { ChatbotController } from './ChatbotController'

const UnauthedMount = observer(function UnauthedMount() {
  const rootStore = useRootStore()

  const route = rootStore.router.currentRoute

  if (route instanceof PublicRoute) {
    const component = route.component as React.ReactNode

    return (
      <SettingsContextProvider>
        <DialogContextProvider>{component}</DialogContextProvider>
      </SettingsContextProvider>
    )
  } else if (route) {
    return (
      <SettingsContextProvider>
        <DialogContextProvider>
          <QAToolsProvider enabled={true}>
            <SchemaErrorNeedsUpdate />
            <LoginPage />
            <DialogMount />
          </QAToolsProvider>
        </DialogContextProvider>
      </SettingsContextProvider>
    )
  } else {
    return <NotFoundPage />
  }
})

function AuthedMount() {
  // Listen for grade sync job completion and show toasts
  useGradeSyncNotification()

  return (
    <UserContextProvider>
      <SettingsContextProvider>
        <DialogContextProvider>
          <QAToolsProvider enabled={true}>
            <SchemaErrorNeedsUpdate />
            <UserAgreements>
              <MobxRouter />
              <DialogMount />
              <AuthedRedirectHandler />
            </UserAgreements>
          </QAToolsProvider>
        </DialogContextProvider>
      </SettingsContextProvider>
    </UserContextProvider>
  )
}

export const Entrypoint = observer(function Entrypoint() {
  const repository = useRepository()

  const [rootStore] = useState(() => {
    // Pass the repository so navigateTo can log navigation analytics from the
    // first (landing) navigation onward.
    const store = new RootStore(repository)

    if (inE2E()) {
      // expose for e2e tests
      // @ts-expect-error -- ignore
      window.rootStore = store
    }

    return store
  })

  const { authenticated, initialized: authInitialized } = useAuthContext()

  useEffect(() => {
    // if we are on logout page and not authenticated, navigate to home, since we are logged out
    setTimeout(() => {
      if (
        rootStore.router.currentRoute?.title === 'logout' &&
        !authenticated &&
        authInitialized
      ) {
        rootStore.navigateTo('home')
      }
    }, 100)
  }, [authenticated, authInitialized, rootStore])

  useEffect(() => {
    // convert hash based routes to history based routes
    const { hash, pathname } = window.location
    // devmode has a special case, it allows hash values
    if (pathname && pathname.match('devmode')) return
    if (hash) {
      window.location.replace(pathname + hash.replace('#', ''))
    }
  })

  const { ltiMode } = useLTIMode()

  // do not continue until authed and feature flags loaded
  if (
    !authInitialized ||
    !repository.initialized ||
    !repository.featureFlags.isLoaded
  )
    return null

  if (!rootStore.hasStartedRouter) {
    return (
      <StoreProvider value={rootStore}>
        <NotFoundPage />
      </StoreProvider>
    )
  }

  if (repository.killSwitch.isEnabled) {
    return (
      <StoreProvider value={rootStore}>
        <KillSwitchEnabledPage />
      </StoreProvider>
    )
  }

  const showAuthedMount = authenticated && !repository.entrypointLocked

  if (repository.shouldTestCrashApp) {
    throw new Error('Crash The App')
  }

  return (
    <>
      <StoreProvider value={rootStore}>
        <ChatbotController />
        <Theme>
          <I18nWrapper>
            <ObjectUrlContextProvider>
              <StorageUrlContextProvider>
                <LazyMotion features={domAnimation}>
                  <PrintingNotSupported />
                  <div className="flex h-dvh w-dvw flex-col md:print:hidden">
                    <ImpersonationBar />
                    <div
                      className={classNames(
                        'relative box-border flex h-full w-full flex-col items-center justify-center',
                        !ltiMode && 'md:p-2'
                      )}
                    >
                      <NewVersionBannerEntry />
                      {showAuthedMount && <AuthedMount />}
                      {!showAuthedMount && <UnauthedMount />}
                    </div>
                  </div>
                </LazyMotion>
              </StorageUrlContextProvider>
            </ObjectUrlContextProvider>
          </I18nWrapper>
        </Theme>
      </StoreProvider>
    </>
  )
})

function PrintingNotSupported() {
  const { tt } = useTranslationTyped()
  return (
    <div className="hidden h-dvh w-dvw flex-col items-center justify-center bg-white text-core-primary md:print:flex">
      {tt.landscape_printing_not_supported()}
    </div>
  )
}
